/**
 * SideNav
 * Left-side navigation bar for the web revamp dashboard.
 * Displays the Instarem logo and nav items per the Figma design (ICFE-4921).
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { MobileDivider, NavList, SideNavContainer } from './SideNav.styles';
import {
  DASHBOARD_URL,
  DEVICE_MOBILE,
  HEADER_FOOTER_IGNORED_URLS,
  TRANSFER_MONEY_URL,
  INDIVIDUAL,
  MULTIPLE_TRANSACTION_URL,
  FLOW_TYPE,
  CONSENT_PERMISSION_URL,
  BASE_CDN_CONSUMER_URL
} from '@Utils/constants';
import {
  getUserFlowReducer,
  selectUiTriggerStateReducer,
  selectUserStatusReducer
} from '@Redux/selectors';
import { toggleSidebar } from '@Redux/actions/ui-trigger/ui-trigger-action';
import { AppDispatch, UserFlowReducer, UserStatusReducer } from '../../../../store.type';
import { useNavItems } from './hooks/useNavItems';
import { useMobileMenuItems } from './hooks/useMobileMenuItems';
import { logEventV2, getPageName } from '@Utils/events/logUserEvents';
import { toggleNewCouponCode } from '@Redux/actions/transaction';
import { clearDashboardCalculatorData } from '@Redux/actions/dashboard';
import { resetTransactionDetails } from '@Redux/actions/common';
import { changeScreen, destroyUserFlowAction } from '@Redux/actions/user-flow';
import { USER_FLOW_JUMP_TO } from '@Redux/action-contants';
import { NavItem, MobileMenuItem } from './SideNav.types';
import {
  EVENT_ROUTE_KEYS,
  EventButtonType,
  EventNamesV2,
  FLOW_TYPE as EVENT_FLOW_TYPE
} from '@Utils/events/eventsConstants';
import { useLogout, LOGOUT_LABEL } from '../../../../hooks/useLogout';

const SideNav: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const mobileMenuItems = useMobileMenuItems();
  const dispatch = useDispatch<AppDispatch>();
  const { account_type: userAccountType } = useSelector(
    selectUserStatusReducer
  ) as UserStatusReducer;
  const {
    timeline,
    screens,
    currentScreen: currentScreenNormalFlow
  } = useSelector(getUserFlowReducer) as UserFlowReducer;
  const { device } = useSelector(selectUiTriggerStateReducer);
  const logout = useLogout();

  if (!HEADER_FOOTER_IGNORED_URLS.includes(pathname)) {
    const isHeaderDisabled = /signin|signup|invite\/|password|logout|feedback\//gi.test(pathname);
    if (isHeaderDisabled || pathname === '/') return null;
  }

  const logCTEvents = (ctaName: string, flowType: string) => {
    logEventV2(EventNamesV2.CTA_CLICKED, {
      type: EventButtonType.BUTTON,
      pageName: getPageName(),
      ctaName,
      flowType
    });
  };

  const onAccountLinkClick = (targetUrl?: string) => {
    dispatch(toggleSidebar());
    dispatch(destroyUserFlowAction());
    if (targetUrl === DASHBOARD_URL) {
      dispatch(toggleNewCouponCode(false));
      dispatch(clearDashboardCalculatorData());
    }
  };

  const handleOnSendMoneyClick = () => {
    // 1. log event
    // toggle new coupon code and clear calculator data use actions and dispatch
    // get the screens, timeline, and currentScreen from userFlowTimeline
    // logic
    // this will fire on send-money menu item click and toggle new coupon code and reset the calculator data

    const analyticsData = {
      currentScreen: currentScreenNormalFlow,
      type: EventButtonType.BUTTON,
      accountType: userAccountType,
      pageName: EVENT_ROUTE_KEYS.SendMoney,
      ctaName: 'SendMoney',
      flowType: FLOW_TYPE.TRANSACTION
    };
    logEventV2(EventNamesV2.CTA_CLICKED, analyticsData);
    dispatch(toggleNewCouponCode(false));
    dispatch(clearDashboardCalculatorData());
    if (pathname === TRANSFER_MONEY_URL && currentScreenNormalFlow === 'send-money') {
      return;
    } else if (pathname === TRANSFER_MONEY_URL && currentScreenNormalFlow !== 'send-money') {
      dispatch(resetTransactionDetails());
      dispatch(changeScreen(USER_FLOW_JUMP_TO, 'send-money', screens, timeline));
    } else {
      dispatch(resetTransactionDetails());
      if (userAccountType === INDIVIDUAL) {
        navigate(TRANSFER_MONEY_URL);
      } else {
        navigate(MULTIPLE_TRANSACTION_URL);
      }
    }
  };

  const handleOtherItemClick = (item: NavItem) => {
    const analyticsData = {
      item,
      currentScreen: currentScreenNormalFlow,
      accountType: userAccountType,
      type: EventButtonType.BUTTON,
      pageName: item.routeKey,
      ctaName: item.key,
      flowType: item.flowType
    };
    logEventV2(EventNamesV2.CTA_CLICKED, analyticsData);
    // we do not want to add other logic like reset calculator data and toggle new coupon code because
    // it is already handled on handleOnSendMoneyClick method
    dispatch(destroyUserFlowAction());
  };

  const handleMenuItemClick = (item: NavItem) => {
    if (device === DEVICE_MOBILE) {
      dispatch(toggleSidebar());
    }
    switch (item.key) {
      case 'send-money': {
        handleOnSendMoneyClick();
        break;
      }
      default:
        handleOtherItemClick(item);
        break;
    }
  };

  const handleMobileItemClick = (item: MobileMenuItem) => (e: React.MouseEvent) => {
    if (item.disabled) {
      e.preventDefault();
      return;
    }
    if (item.key === 'home') {
      logCTEvents('Dashboard', EVENT_FLOW_TYPE.DASHBOARD);
      onAccountLinkClick(DASHBOARD_URL);
    } else {
      onAccountLinkClick();
    }
  };

  if (pathname === CONSENT_PERMISSION_URL) return null;
  return (
    <SideNavContainer>
      <NavList>
        {navItems.map((item) => {
          const isActive = pathname.startsWith(item.route);
          const isMailto = item.route.startsWith('mailto:');
          const className = `nav-list__item${isActive ? ' active' : ''}`;
          const content = (
            <>
              <img alt={item.label} src={isActive ? item.activeIcon : item.icon} />
              <span>{item.label}</span>
            </>
          );
          return (
            <li key={item.key} onClick={() => handleMenuItemClick(item)}>
              {isMailto ? (
                <a href={item.route} className={className}>
                  {content}
                </a>
              ) : (
                <Link className={className} to={item.route}>
                  {content}
                </Link>
              )}
            </li>
          );
        })}
        {device === DEVICE_MOBILE && (
          <>
            {mobileMenuItems.length > 0 && (
              <>
                <MobileDivider />
                {mobileMenuItems.map((item) => (
                  <li key={item.key}>
                    <Link
                      className={`nav-list__item${item.disabled ? ' disabled' : ''}`}
                      to={item.route}
                      onClick={handleMobileItemClick(item)}
                    >
                      <img alt={item.label} src={item.icon} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </>
            )}
            <li className="nav-list__item" onClick={logout}>
              <img alt="logout" src={`${BASE_CDN_CONSUMER_URL}web-revamp/logout-icon.svg`} />
              <span>{LOGOUT_LABEL}</span>
            </li>
          </>
        )}
      </NavList>
    </SideNavContainer>
  );
};

export default SideNav;
